import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { SectionIntro } from "@/components/site/SectionHead";
import { Reveal, Stagger, StaggerItem } from "@/components/site/motion";
import { PillLink } from "@/components/site/Buttons";
import { SpotCard } from "@/components/site/ClipFrame";
import { ApplicationForm } from "@/components/forms/ApplicationForm";
import { ROLES, PROBLEMS, ETHOS } from "@/content/roles";
import { scrollToTarget } from "@/components/site/SmoothScroll";

const RoleRow = ({ role, i }) => (
  <StaggerItem>
    <Link to={`/careers/${role.slug}`} className="group relative grid items-baseline gap-3 border-b border-white/10 py-8 transition-colors hover:bg-white/[0.015] lg:grid-cols-12" data-testid={`role-row-${role.slug}`}>
      <span className="mono text-[12px] text-fg-3 lg:col-span-1">0{i + 1}</span>
      <span className="text-3xl font-[540] tracking-[-0.03em] text-fg transition-transform duration-500 group-hover:translate-x-1 sm:text-4xl lg:col-span-5">{role.title}</span>
      <span className="text-[15px] leading-relaxed text-fg-2 lg:col-span-4">{role.line}</span>
      <span className="flex items-center gap-3 lg:col-span-2 lg:justify-end">
        <span className="eyebrow text-fg-3">{role.discipline}</span>
        <span className="grid h-10 w-10 place-items-center rounded-full border border-white/15 transition-[transform,background-color,color] duration-500 group-hover:rotate-45 group-hover:bg-signal group-hover:text-ink"><ArrowUpRight className="h-4 w-4" /></span>
      </span>
    </Link>
  </StaggerItem>
);

export default function Careers() {
  return (
    <div data-testid="careers-page">
      <PageHero
        testId="careers-hero"
        eyebrow="Careers"
        lines={["Build ships", "no one has to", "live aboard."]}
        text="We're hiring builders in robotics, Physical AI, controls, naval architecture, and mechanical and electrical engineering: people who want their work to go to sea."
        image="/media/engine.jpg"
        imageCaption="Machinery spaces: built for people, until now"
      >
        <Reveal delay={0.45} className="mt-10 flex flex-wrap gap-3">
          <PillLink onClick={() => scrollToTarget("#roles")} testId="careers-see-roles">See open roles</PillLink>
          <PillLink variant="ghost" onClick={() => scrollToTarget("#open-application")} testId="careers-open-application-cta">Don't fit a role?</PillLink>
        </Reveal>
      </PageHero>

      <section className="shell py-24 sm:py-32">
        <SectionIntro label="How we work" title="Hard problems. Real agency. Physical consequences." />
        <Stagger className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {ETHOS.map((e, i) => (
            <StaggerItem key={e.k}>
              <SpotCard className="h-full p-7" testId={`ethos-${i}`}>
                <span className="mono text-[12px] text-signal">0{i + 1}</span>
                <h3 className="mt-10 text-xl font-[540] leading-snug tracking-[-0.02em] text-fg">{e.k}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-fg-2">{e.v}</p>
              </SpotCard>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      <section className="bg-ink-2 py-24 sm:py-32">
        <div className="shell">
          <SectionIntro label="The work" title="Problems you could own." text="None of these is solved. Most have never been tried on a cargo ship." />
          <Stagger className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.08] md:grid-cols-2 lg:grid-cols-3">
            {PROBLEMS.map((p) => (
              <StaggerItem key={p.k} className="bg-ink-2 p-7 sm:p-8">
                <h3 className="text-xl font-[540] tracking-[-0.02em] text-fg">{p.k}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-fg-2">{p.v}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section id="roles" className="shell py-24 sm:py-32" data-testid="roles-section">
        <SectionIntro label="Open roles" title="First roles." text="Full job descriptions are coming. If one of these is your problem, apply now: we read every application." />
        <Stagger className="mt-14 border-t border-white/10">
          {ROLES.map((r, i) => <RoleRow key={r.slug} role={r} i={i} />)}
        </Stagger>
      </section>

      <section id="open-application" className="border-t border-white/[0.06] bg-ink-2 py-24 sm:py-32" data-testid="open-application-section">
        <div className="shell grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal><p className="eyebrow text-signal">Open application</p></Reveal>
            <Reveal delay={0.05}><h2 className="mt-5 text-[2.2rem] font-[540] leading-[1.02] tracking-[-0.035em] text-fg sm:text-5xl">Exceptional, but not on the list?</h2></Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-lg leading-relaxed text-fg-2">AI, robotics, controls, world models, maritime systems or something adjacent. If you've built hard things and want to build this, tell us.</p>
              <p className="mt-6 text-[15px] leading-relaxed text-fg-3">You'll work directly with the founders, Hash and Kelp.</p>
            </Reveal>
          </div>
          <Reveal className="lg:col-span-7" delay={0.1}>
            <div className="rounded-3xl border border-white/[0.08] bg-ink p-6 sm:p-10"><ApplicationForm role="open" /></div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
